import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

const MOVE_PX = 5;

const imageFromEvent = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return null;
  if (target.closest(".rte-image-resize, .rte-media-chrome, figcaption, .rte-image-lightbox")) {
    return null;
  }
  const img = target.closest("figure.rte-image img");
  if (!img || !img.closest(".eval-rich-content")) return null;
  return img;
};

const captionFromImage = (img) => {
  const fig = img.closest("figure.rte-image");
  const cap = [...(fig?.children || [])].find((el) => el.matches?.("figcaption.rte-caption"));
  if (!cap) return null;
  const label = cap.querySelector(".rte-caption-label")?.textContent?.replace(/\s+/g, " ").trim() || "";
  const textEl = cap.querySelector(".rte-caption-text");
  const text = (textEl ? textEl.textContent : cap.textContent)?.replace(/\s+/g, " ").trim() || "";
  const body = textEl ? text : text.replace(label, "").trim();
  if (!label && !body) return null;
  return {
    label,
    text: body,
    align: cap.getAttribute("data-rte-caption-align") || fig.getAttribute("data-rte-caption-align") || "center",
    size: cap.getAttribute("data-rte-caption-size") || fig.getAttribute("data-rte-caption-size") || "",
  };
};

const scrollableOverflow = (value) => value === "auto" || value === "scroll" || value === "overlay";

const rememberScroll = (fromEl) => {
  const saved = [];
  const seen = new Set();
  const push = (el, top, left, isWindow) => {
    if (!el || seen.has(el)) return;
    seen.add(el);
    saved.push({ el, top, left, isWindow });
  };
  let node = fromEl?.parentElement || null;
  while (node) {
    const style = window.getComputedStyle(node);
    const canScroll = scrollableOverflow(style.overflowY) || scrollableOverflow(style.overflowX);
    if (
      canScroll &&
      (node.scrollHeight > node.clientHeight + 1 ||
        node.scrollWidth > node.clientWidth + 1 ||
        node.scrollTop > 0 ||
        node.scrollLeft > 0)
    ) {
      push(node, node.scrollTop, node.scrollLeft, false);
    }
    node = node.parentElement;
  }
  const scrolling = document.scrollingElement;
  if (scrolling) push(scrolling, scrolling.scrollTop, scrolling.scrollLeft, false);
  push(window, window.scrollY || 0, window.scrollX || 0, true);
  return saved;
};

const applyScroll = (saved) => {
  if (!saved) return;
  saved.forEach((item) => {
    if (item.isWindow) {
      if (!/jsdom/i.test(window.navigator?.userAgent || "")) {
        window.scrollTo(item.left, item.top);
      }
      return;
    }
    if (item.el?.isConnected) {
      item.el.scrollTop = item.top;
      item.el.scrollLeft = item.left;
    }
  });
};

const stackLightbox = () => {
  const openModals = document.querySelectorAll(".modal.show").length;
  if (openModals <= 1) return;
  window.setTimeout(() => {
    const backdrops = document.querySelectorAll(".modal-backdrop:not(.stacked)");
    const latest = backdrops[backdrops.length - 1];
    const modal = document.querySelector(".modal.rte-image-lightbox");
    if (latest) {
      latest.style.zIndex = String(1050 + 10 * openModals);
      latest.classList.add("stacked");
    }
    if (modal) modal.style.zIndex = String(1055 + 10 * openModals);
  }, 0);
};

export default function RichImageLightbox() {
  const [image, setImage] = useState(null);
  const downRef = useRef(null);
  const scrollRef = useRef(null);

  const keepScroll = () => {
    const saved = scrollRef.current;
    applyScroll(saved);
    window.setTimeout(() => applyScroll(saved), 0);
    window.requestAnimationFrame(() => applyScroll(saved));
  };

  useEffect(() => {
    const onDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      const img = imageFromEvent(event);
      downRef.current = img ? { x: event.clientX, y: event.clientY, img } : null;
    };
    const onClick = (event) => {
      const img = imageFromEvent(event);
      const down = downRef.current;
      downRef.current = null;
      if (!img || !down || down.img !== img) return;
      if (document.documentElement.classList.contains("rte-image-resizing")) return;
      const dx = event.clientX - down.x;
      const dy = event.clientY - down.y;
      if (dx * dx + dy * dy > MOVE_PX * MOVE_PX) return;
      const src = img.currentSrc || img.getAttribute("src") || "";
      if (!src) return;
      event.preventDefault();
      event.stopPropagation();
      scrollRef.current = rememberScroll(img);
      setImage({
        src,
        alt: img.getAttribute("alt") || "",
        caption: captionFromImage(img),
      });
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onClick, true);
    };
  }, []);

  return (
    <Modal
      show={!!image}
      onHide={() => {
        setImage(null);
        keepScroll();
      }}
      onShow={() => {
        stackLightbox();
        keepScroll();
      }}
      onExited={() => {
        if (document.querySelectorAll(".modal.show").length > 0) {
          document.body.classList.add("modal-open");
        }
        keepScroll();
      }}
      size="xl"
      centered
      enforceFocus={false}
      restoreFocusOptions={{ preventScroll: true }}
      className={`rte-image-lightbox${image?.caption ? " has-caption" : ""}`}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title as="h2" className="h5 mb-0">{image?.alt || "Image"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {image ? (
          <figure className="rte-image-lightbox-figure eval-rich-content">
            <img src={image.src} alt={image.alt} />
            {image.caption ? (
              <figcaption
                className="rte-caption"
                data-rte-caption-align={image.caption.align}
                data-rte-caption-size={image.caption.size || undefined}
              >
                {image.caption.label ? (
                  <span className="rte-caption-label">{image.caption.label}</span>
                ) : null}
                {image.caption.text ? (
                  <span className="rte-caption-text">{image.caption.text}</span>
                ) : null}
              </figcaption>
            ) : null}
          </figure>
        ) : null}
      </Modal.Body>
    </Modal>
  );
}
